import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import { API } from '../App';
import { toast } from 'sonner';
import { Clock, LogIn, LogOut, Calendar, Search, Users, AlertTriangle, CheckCircle, XCircle, TrendingUp, Plus, Pencil, Loader2, RotateCcw } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';

import {
  PRESENCE_REASONS, presenceReasonLabel, isHrMarked, countsAsPresent, isAttendedStatus, showsHrMarkedPill,
  formatAttendanceTime, attendanceMinutes, WORK_START_MIN,
  formatWorkedHours, workedMinutes, isEarlyDeparture, isOnLeaveOnDate, findPublicHoliday,
  deriveAttendanceStatus, buildMonthlyDays, STATUS_LABELS,
} from '../lib/attendanceUtils';
import { EmployeePicker } from '../components/attendance/EmployeePicker';
import { EmployeeMonthlyReport } from '../components/attendance/EmployeeMonthlyReport';

const HrMarkedPill = ({ record }) => {
  if (!showsHrMarkedPill(record)) return null;
  const reason = presenceReasonLabel(record.presence_reason);
  return (
    <span
      data-testid={`hr-marked-${record.attendance_id}`}
      className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-violet-100 text-violet-700 dark:bg-violet-900/40 dark:text-violet-300 w-fit"
      title={record.notes || 'Marked present by HR on the employee\'s behalf'}
    >
      Marked by HR{reason ? ` · ${reason}` : ''}
    </span>
  );
};


export default function Attendance() {
  const [attendance, setAttendance] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedEmployee, setSelectedEmployee] = useState('');
  const [dateFilter, setDateFilter] = useState(new Date().toISOString().split('T')[0]);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  // Weekly / Monthly summary toggle. Hidden in default daily view; expanding it
  // computes an aggregated summary over the chosen period.
  const [summaryRange, setSummaryRange] = useState('off'); // 'off' | 'weekly' | 'monthly'
  // Department filter (HR view only). 'all' shows everyone.
  const [departmentFilter, setDepartmentFilter] = useState('all');
  // Monthly Attendance Report view. When `monthlyReport` is true the page
  // switches its main table to a month-wide attendance report.
  const [monthlyReport, setMonthlyReport] = useState(false);
  const todayYM = new Date().toISOString().slice(0, 7);
  const [reportMonth, setReportMonth] = useState(todayYM); // YYYY-MM
  const todayYMD = new Date().toISOString().split('T')[0];
  // Monthly report: '' = all employees, otherwise a single employee's day-by-day report.
  const [reportEmployeeId, setReportEmployeeId] = useState('');
  const attendanceFiltersActive = dateFilter !== todayYMD || Boolean(searchQuery) || statusFilter !== 'all'
    || departmentFilter !== 'all' || summaryRange !== 'off' || monthlyReport || reportMonth !== todayYM || Boolean(reportEmployeeId);
  const resetAttendanceFilters = () => {
    setDateFilter(todayYMD); setSearchQuery(''); setStatusFilter('all'); setDepartmentFilter('all');
    setSummaryRange('off'); setMonthlyReport(false); setReportMonth(todayYM); setReportEmployeeId('');
  };
  const openEmployeeMonthlyReport = (employeeId) => {
    setReportEmployeeId(employeeId);
    setMonthlyReport(true);
    setReportMonth((dateFilter || todayYMD).slice(0, 7));
  };
  // HR-only: approved leaves + public holidays so we can render "On Leave"
  // and "Public Holiday" statuses on the attendance grid.
  const [approvedLeaves, setApprovedLeaves] = useState([]);
  const [publicHolidays, setPublicHolidays] = useState([]);
  // HR-only: add/edit attendance dialog state.
  const [attDialogOpen, setAttDialogOpen] = useState(false);
  const [attDialogMode, setAttDialogMode] = useState('add'); // 'add' | 'edit'
  const [attForm, setAttForm] = useState({
    attendance_id: '',
    employee_id: '',
    date: new Date().toISOString().slice(0, 10),
    clock_in: '',
    clock_out: '',
    status: 'present',
    notes: '',
    presence_reason: '',
    // Iter90 (Shikha 10-Jul-2026) — Explicit opt-in to log attendance
    // on Sunday (Malaysia weekly rest day). Checkbox only shown in the
    // dialog when the selected date is a Sunday. Backend enforces the
    // guard regardless.
    allow_rest_day: false,
  });
  const [attSaving, setAttSaving] = useState(false);
  
  const user = JSON.parse(localStorage.getItem('hrms_user') || '{}');
  const isHRAdmin = ['admin', 'hr_manager', 'super_user'].includes(user.role);
  const isManager = ['manager', 'department_head'].includes(user.role);
  const isEmployee = user.role === 'employee';
  // Iter85 — Pre-boarding employees (status === 'pending_joining') don't
  // have attendance obligation until HR activates them. Hide the
  // clock-in/out UI for them so they don't get confused; backend also
  // hard-blocks clock-in for these users.
  const isPreboarding = user.status === 'pending_joining';

  useEffect(() => {
    fetchData();
  }, []);

  // For regular employees, auto-set their employee_id
  useEffect(() => {
    if (isEmployee && user.employee_id) {
      setSelectedEmployee(user.employee_id);
    }
  }, [isEmployee, user.employee_id]);

  const fetchData = async () => {
    try {
      // Regular employees only see their own data
      if (isEmployee) {
        const [attendanceRes, leaveRes] = await Promise.all([
          axios.get(`${API}/attendance?employee_id=${user.employee_id}`),
          axios.get(`${API}/leave/applications`).catch(() => ({ data: [] })),
        ]);
        setAttendance(attendanceRes.data);
        setEmployees([{ employee_id: user.employee_id, full_name: user.full_name }]);
        setSelectedEmployee(user.employee_id);
        setApprovedLeaves((leaveRes.data || []).filter((l) => l.status === 'approved'));
      } else {
        // Managers fetch their team, HR/Admin fetch all
        const employeeEndpoint = isManager 
          ? `${API}/my-team` 
          : `${API}/employees`;
        
        const [attendanceRes, employeesRes, leaveRes] = await Promise.all([
          axios.get(`${API}/attendance`),
          axios.get(employeeEndpoint),
          axios.get(`${API}/leave/applications`).catch(() => ({ data: [] })),
        ]);
        setAttendance(attendanceRes.data);
        setEmployees(employeesRes.data);
        setApprovedLeaves((leaveRes.data || []).filter((l) => l.status === 'approved'));
        
        // For managers, default to their own employee_id for clock in/out
        if (isManager && user.employee_id) {
          setSelectedEmployee(user.employee_id);
        } else if (employeesRes.data.length > 0) {
          setSelectedEmployee(employeesRes.data[0].employee_id);
        }
      }
      // Public holidays — fetch for current and prior year so monthly views
      // far in the past still resolve.
      const now = new Date();
      const years = [now.getFullYear(), now.getFullYear() - 1];
      const all = await Promise.all(
        years.map((y) => axios.get(`${API}/public-holidays?year=${y}`).catch(() => ({ data: [] })))
      );
      setPublicHolidays(all.flatMap((r) => r.data || []));
    } catch (error) {
      toast.error('Failed to fetch data');
    } finally {
      setLoading(false);
    }
  };

  const handleClockIn = async () => {
    // Iter55 (Ankur 25-Jun-2026) — Clock-in/out always defaults to the
    // logged-in user's OWN employee_id regardless of role. Previously HR,
    // Payroll Officer and Admin users had `clockEmployee = selectedEmployee`
    // which auto-populated to the FIRST employee in the list (typically
    // John Doe), so clicking Clock In tried to clock in someone else.
    // The backend then correctly returned "Already clocked in today" but
    // the user got a confusing error because they thought they were
    // clocking themselves in.
    const clockEmployee = user.employee_id;
    if (!clockEmployee) {
      toast.error('Your account is not linked to an employee profile, so attendance cannot be recorded.');
      return;
    }
    try {
      const response = await axios.post(`${API}/attendance/clock-in?employee_id=${clockEmployee}`);
      // Iter88m — If the backend auto-resolved an approved leave for this
      // date, show a longer info toast so the employee actually reads it.
      if (response.data.leave_auto_resolved) {
        toast.success(response.data.message, { duration: 8000 });
      } else {
        toast.success(response.data.message);
      }
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to clock in');
    }
  };

  const handleClockOut = async () => {
    // Iter55 — same self-only fix as handleClockIn above.
    const clockEmployee = user.employee_id;
    if (!clockEmployee) {
      toast.error('Your account is not linked to an employee profile, so attendance cannot be recorded.');
      return;
    }
    try {
      const response = await axios.post(`${API}/attendance/clock-out?employee_id=${clockEmployee}`);
      toast.success(response.data.message);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to clock out');
    }
  };

  // Filter attendance by date
  const dateFilteredAttendance = useMemo(() => {
    return attendance.filter(record => {
      // Iter76 — Guard against records with missing/invalid dates so
      // a single bad row can't crash the entire Attendance page.
      if (!record.date) return false;
      try {
        const recordDate = new Date(record.date).toISOString().split('T')[0];
        return recordDate === dateFilter;
      } catch {
        return false;
      }
    });
  }, [attendance, dateFilter]);

  // Calculate attendance statistics
  const attendanceStats = useMemo(() => {
    const stats = {
      total: employees.length,
      present: 0,
      late: 0,
      early: 0,
      absent: 0,
      notClocked: 0,
      onLeave: 0,
      halfDay: 0,
    };

    const clockedInEmployees = new Set(dateFilteredAttendance.map(a => a.employee_id));

    dateFilteredAttendance.forEach(record => {
      // Iter74 (Shikha 07-Jul-2026) — summary card and the row-level filter
      // must agree on each record's status. Previously this block hard-coded
      // the buckets from `record.clock_in` regardless of any HR manual edit,
      // so a record whose HR-persisted status = 'present' but clock_in was
      // after 9 AM was counted as `late` here while the filter returned it
      // as `present` — giving the tester "Late Arrivals: 1" in the card and
      // "0 records found" in the table. Delegate to `getAttendanceStatus`
      // so both surfaces read the same value.
      const st = getAttendanceStatus(record);
      if (st === 'on_leave') { stats.onLeave++; return; }
      if (st === 'half_day') { stats.halfDay++; return; }
      if (st === 'absent' || st === 'missing_clock_in') { stats.absent++; return; }
      if (st === 'late') { stats.late++; return; }
      if (st === 'early') { stats.early++; return; }
      // Default bucket — treats 'on_time' and any manually-set 'present'
      // status as present.
      stats.present++;
    });

    // Count employees who haven't clocked in (only matters when no override exists).
    employees.forEach(emp => {
      if (!clockedInEmployees.has(emp.employee_id)) {
        stats.notClocked++;
      }
    });

    // Absent total = explicit absent + nobody-clocked-in.
    stats.absent = stats.absent + stats.notClocked;

    return stats;
  }, [dateFilteredAttendance, employees, dateFilter]);

  // Categorize each attendance record.
  // Iter78 (Shikha 07-Jul-2026) — Was `const getAttendanceStatus = (record) => …`,
  // which put it in the temporal dead zone when the `attendanceStats` useMemo
  // above called it during the first render, throwing
  // `ReferenceError: Cannot access 'getAttendanceStatus' before initialization`.
  // Switching to a function declaration hoists the binding to the top of the
  // component body and the call inside `attendanceStats` resolves cleanly.
  function getAttendanceStatus(record) {
    return deriveAttendanceStatus(record);
  }

  // Returns true when HR has explicitly set a manual status on this record.
  // Used to suppress the auto leave/holiday badge so manual edits actually
  // surface in the UI.
  // Iter78 — also converted to a function declaration for consistency /
  // hoisting safety in case a future useMemo above calls it.
  function hasManualStatusOverride(record) {
    const p = (record?.status || '').toLowerCase();
    if (!p) return false;
    if (Boolean(record?.updated_by)) return true;
    // Iter53 — `present` added so a manual edit "On Leave" → "Present"
    // (Bug 3, Ankur 24-Jun-2026) actually suppresses the auto-detected
    // "On Leave" badge in the list view. Previously missing — the badge
    // stayed visible because the frontend assumed `present` could only be
    // auto-derived.
    return ['absent', 'half_day', 'on_leave', 'late', 'early', 'on_time', 'present'].includes(p);
  }

  // ---- Weekly / Monthly summary aggregation ----
  // Computes total worked hours, days present, late arrivals and early
  // departures over the chosen window. Honours role boundaries:
  //   - Employee: only their own records.
  //   - Manager: their team's records.
  //   - HR / Admin: company-wide records.
  const summaryData = useMemo(() => {
    if (summaryRange === 'off') return null;

    const today = new Date();
    const start = new Date();
    if (summaryRange === 'weekly') {
      start.setDate(today.getDate() - 6); // include today => 7-day window
    } else {
      start.setDate(today.getDate() - 29); // 30-day window
    }
    start.setHours(0, 0, 0, 0);
    const end = new Date(today);
    end.setHours(23, 59, 59, 999);

    // Employee already gets a scoped attendance list; HR/Manager get all.
    let scoped = attendance;
    if (isEmployee && user.employee_id) {
      scoped = scoped.filter((r) => r.employee_id === user.employee_id);
    } else if (isManager) {
      const teamIds = new Set(employees.map((e) => e.employee_id));
      scoped = scoped.filter((r) => teamIds.has(r.employee_id));
    }

    const inRange = scoped.filter((r) => {
      const d = new Date(r.date || r.clock_in || r.created_at);
      return d >= start && d <= end;
    });

    const totalMinutes = inRange.reduce((sum, r) => sum + workedMinutes(r), 0);
    const daysPresentSet = new Set(
      inRange
        .filter(countsAsPresent)
        .map((r) => `${r.employee_id}::${(r.date || r.clock_in).slice(0, 10)}`)
    );
    const lateArrivals = inRange.filter((r) => {
      // Iter77 — wall-clock minutes comparison.
      if (!r.clock_in) return false;
      const ci = attendanceMinutes(r.clock_in);
      return ci !== null && ci > WORK_START_MIN;
    }).length;
    const earlyDepartures = inRange.filter(isEarlyDeparture).length;

    // Absences = scheduled-but-no-clock-in. We don't have a shift system, so we
    // approximate as 0 for employee view (it's their record), and for HR view
    // we count (#weekdays in window × #employees) − distinct clock-in days.
    let absences = 0;
    if (isHRAdmin || isManager) {
      const empCount = employees.length || 0;
      let weekdayCount = 0;
      const cur = new Date(start);
      while (cur <= end) {
        const dow = cur.getDay();
        if (dow !== 0 && dow !== 6) weekdayCount += 1;
        cur.setDate(cur.getDate() + 1);
      }
      absences = Math.max(0, weekdayCount * empCount - daysPresentSet.size);
    }

    const hrs = Math.floor(totalMinutes / 60);
    const mins = totalMinutes % 60;

    return {
      label: summaryRange === 'weekly' ? 'Last 7 days' : 'Last 30 days',
      windowStart: start.toISOString().slice(0, 10),
      windowEnd: end.toISOString().slice(0, 10),
      totalWorkedHours: `${hrs}h ${mins}m`,
      daysPresent: daysPresentSet.size,
      lateArrivals,
      earlyDepartures,
      absences,
    };
  }, [summaryRange, attendance, employees, isEmployee, isManager, isHRAdmin, user.employee_id]);

  // Filter attendance records
  const filteredAttendance = useMemo(() => {
    let filtered = dateFilteredAttendance;
    
    // Apply status filter
    if (statusFilter !== 'all') {
      filtered = filtered.filter(record => getAttendanceStatus(record) === statusFilter);
    }
    
    // Apply department filter (HR-only; resolves dept from joined employee record)
    if (departmentFilter !== 'all') {
      filtered = filtered.filter((record) => {
        const employee = employees.find((e) => e.employee_id === record.employee_id);
        return (employee?.department || 'Unassigned') === departmentFilter;
      });
    }

    // Apply search filter
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(record => {
        const employee = employees.find(e => e.employee_id === record.employee_id);
        return (
          employee?.full_name?.toLowerCase().includes(query) ||
          record.employee_id?.toLowerCase().includes(query) ||
          employee?.email?.toLowerCase().includes(query)
        );
      });
    }
    
    return filtered;
  }, [dateFilteredAttendance, statusFilter, departmentFilter, searchQuery, employees]);

  // Unique department list (HR filter dropdown)
  const departmentList = useMemo(() => {
    const set = new Set();
    employees.forEach((e) => {
      if (e?.department) set.add(e.department);
    });
    return Array.from(set).sort();
  }, [employees]);

  // Monthly Attendance Report: records inside the selected month, honouring
  // department + search + status filters. Used by the report table & export.
  const monthlyReportRows = useMemo(() => {
    const ym = reportMonth || todayYM;
    let scoped = attendance;
    if (isEmployee && user.employee_id) {
      scoped = scoped.filter((r) => r.employee_id === user.employee_id);
    } else if (isManager) {
      const teamIds = new Set(employees.map((e) => e.employee_id));
      scoped = scoped.filter((r) => teamIds.has(r.employee_id));
    }
    let rows = scoped.filter((r) => (r.date || r.clock_in || '').slice(0, 7) === ym);
    if (departmentFilter !== 'all') {
      rows = rows.filter((r) => {
        const emp = employees.find((e) => e.employee_id === r.employee_id);
        return (emp?.department || 'Unassigned') === departmentFilter;
      });
    }
    if (statusFilter !== 'all') {
      rows = rows.filter((r) => getAttendanceStatus(r) === statusFilter);
    }
    if (searchQuery) {
      const q = searchQuery.toLowerCase();
      rows = rows.filter((r) => {
        const emp = employees.find((e) => e.employee_id === r.employee_id);
        return (
          emp?.full_name?.toLowerCase().includes(q) ||
          r.employee_id?.toLowerCase().includes(q) ||
          emp?.email?.toLowerCase().includes(q)
        );
      });
    }
    // Sort newest first
    rows.sort((a, b) => (b.date || '').localeCompare(a.date || ''));
    return rows;
  }, [attendance, employees, reportMonth, todayYM, departmentFilter, statusFilter, searchQuery, isEmployee, isManager, user.employee_id]);

  // Single-employee monthly report (HR picks one, employees always see their own).
  const reportEmployee = useMemo(() => {
    const id = isEmployee ? user.employee_id : reportEmployeeId;
    return id ? employees.find((e) => e.employee_id === id) || { employee_id: id, full_name: user.full_name } : null;
  }, [isEmployee, user.employee_id, user.full_name, reportEmployeeId, employees]);
  const reportEmployeeRecords = useMemo(() => {
    if (!reportEmployee) return [];
    const ym = reportMonth || todayYM;
    return attendance.filter((r) => r.employee_id === reportEmployee.employee_id && (r.date || r.clock_in || '').slice(0, 7) === ym);
  }, [attendance, reportEmployee, reportMonth, todayYM]);

  // Export currently-visible attendance rows to .xlsx
  const exportAttendanceToExcel = async () => {
    // Lazy-load xlsx to avoid bloating the initial bundle.
    const XLSX = await import('xlsx');
    if (monthlyReport && reportEmployee) {
      const days = buildMonthlyDays({
        employeeId: reportEmployee.employee_id, month: reportMonth || todayYM,
        records: reportEmployeeRecords, approvedLeaves, publicHolidays,
      });
      const rows = days.map((d) => ({
        'Date': d.date,
        'Day': ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'][d.dow],
        'Clock In': formatAttendanceTime(d.record?.clock_in, { withMeridiem: true }),
        'Clock Out': formatAttendanceTime(d.record?.clock_out, { withMeridiem: true }),
        'Total Hours': d.hours,
        'Status': STATUS_LABELS[d.status] || d.status,
        'Marked By HR': d.hrMarked ? 'Yes' : 'No',
        'Reason': d.presenceReason,
        'Early Departure': d.earlyDeparture ? 'Yes' : 'No',
        'Remarks': [d.holiday?.name, d.leaveLabel, d.record?.notes].filter(Boolean).join(' · '),
      }));
      const ws = XLSX.utils.json_to_sheet(rows);
      ws['!cols'] = [{ wch: 12 }, { wch: 6 }, { wch: 12 }, { wch: 12 }, { wch: 12 }, { wch: 16 }, { wch: 12 }, { wch: 22 }, { wch: 14 }, { wch: 30 }];
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, 'Monthly Attendance');
      const fname = `attendance_${reportEmployee.employee_id}_${reportMonth || todayYM}.xlsx`;
      XLSX.writeFile(wb, fname);
      toast.success(`Exported ${reportEmployee.full_name}'s ${reportMonth || todayYM} report to ${fname}`);
      return;
    }
    const rows = (monthlyReport ? monthlyReportRows : filteredAttendance).map((r) => {
      const emp = employees.find((e) => e.employee_id === r.employee_id);
      const standardEnd = (r.clock_out ? new Date(r.clock_out) : null);
      return {
        'Employee Name': emp?.full_name || 'Unknown',
        'Employee ID': r.employee_id || '',
        'Department': emp?.department || 'Unassigned',
        'Date': (r.date || r.clock_in || '').slice(0, 10),
        'Clock In': formatAttendanceTime(r.clock_in, { withMeridiem: true }),
        'Clock Out': formatAttendanceTime(r.clock_out, { withMeridiem: true }),
        'Total Hours': formatWorkedHours(r),
        'Status': getAttendanceStatus(r),
        'Marked By HR': isHrMarked(r) ? 'Yes' : 'No',
        'Reason': presenceReasonLabel(r.presence_reason),
        'Early Departure': isEarlyDeparture(r) && standardEnd ? 'Yes' : 'No',
      };
    });
    if (rows.length === 0) {
      toast.error('No attendance records to export with current filters');
      return;
    }
    const ws = XLSX.utils.json_to_sheet(rows);
    ws['!cols'] = [
      { wch: 24 }, { wch: 14 }, { wch: 16 }, { wch: 12 },
      { wch: 12 }, { wch: 12 }, { wch: 12 }, { wch: 16 }, { wch: 12 }, { wch: 22 }, { wch: 14 },
    ];
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Attendance');
    const tag = monthlyReport ? `monthly_${reportMonth}` : `daily_${dateFilter}`;
    const fname = `attendance_export_${tag}.xlsx`;
    XLSX.writeFile(wb, fname);
    toast.success(`Exported ${rows.length} record${rows.length === 1 ? '' : 's'} to ${fname}`);
  };

  // ----- HR: Add / Edit attendance dialog handlers -----
  const openAddAttendance = (presetEmployeeId, presetDate) => {
    setAttDialogMode('add');
    setAttForm({
      attendance_id: '',
      employee_id: (typeof presetEmployeeId === 'string' && presetEmployeeId) || employees[0]?.employee_id || '',
      date: (typeof presetDate === 'string' && presetDate) || dateFilter,
      clock_in: '',
      clock_out: '',
      status: 'present',
      notes: '',
      presence_reason: '',
    });
    setAttDialogOpen(true);
  };

  const openEditAttendance = (record) => {
    // Iter76 — Defensive parser that matches the rendering rule: pull the
    // HH:MM portion verbatim from the ISO/short value WITHOUT any timezone
    // conversion. Earlier version used `new Date(...).toISOString()` which
    // (a) crashed on plain `08:42` and (b) silently shifted timezones for
    // valid ISO timestamps. The Attendance table now never shifts time and
    // neither does this prefill. Reported by Ankur 02-Jun-2026.
    const safeTime = (raw) => {
      if (!raw) return '';
      const s = String(raw).trim();
      // Plain HH:MM or HH:MM:SS — already wall-clock.
      let m = s.match(/^(\d{1,2}):(\d{2})(?::\d{2})?$/);
      if (m) {
        return `${String(parseInt(m[1], 10)).padStart(2, '0')}:${m[2]}`;
      }
      // ISO timestamp — pull HH:MM right after the `T`.
      m = s.match(/T(\d{2}):(\d{2})/);
      if (m) return `${m[1]}:${m[2]}`;
      return '';
    };
    setAttDialogMode('edit');
    setAttForm({
      attendance_id: record.attendance_id,
      employee_id: record.employee_id,
      date: (record.date || '').slice(0, 10),
      clock_in: safeTime(record.clock_in),
      clock_out: safeTime(record.clock_out),
      status: record.status || 'present',
      notes: record.notes || '',
      presence_reason: record.presence_reason || '',
    });
    setAttDialogOpen(true);
  };

  const needsPresenceReason = isAttendedStatus(attForm.status) && !(attForm.clock_in || '').trim();

  const submitAttendanceDialog = async () => {
    if (!attForm.employee_id || !attForm.date) {
      toast.error('Employee and date are required');
      return;
    }
    if (needsPresenceReason && !attForm.presence_reason) {
      toast.error('Please select a reason for recording this attendance without a clock-in');
      return;
    }
    setAttSaving(true);
    try {
      const body = {
        employee_id: attForm.employee_id,
        date: attForm.date,
        clock_in: attForm.clock_in || null,
        clock_out: attForm.clock_out || null,
        status: attForm.status,
        notes: attForm.notes,
        presence_reason: isAttendedStatus(attForm.status) ? (attForm.presence_reason || null) : null,
        // Iter90 — Pass through the Sunday opt-in flag; ignored by the
        // backend for non-Sunday dates.
        allow_rest_day: Boolean(attForm.allow_rest_day),
      };
      let res;
      if (attDialogMode === 'add') {
        res = await axios.post(`${API}/attendance`, body);
      } else {
        res = await axios.put(`${API}/attendance/${attForm.attendance_id}`, body);
      }
      // Verify the backend actually persisted the update — only show success
      // toast once we have a 2xx + status='success' from the server.
      if (res?.status >= 200 && res?.status < 300 && (res?.data?.status === 'success' || res?.data?.attendance)) {
        // Await the refetch so the table & summary cards reflect the new state
        // before we close the dialog and show the toast.
        await fetchData();
        toast.success(attDialogMode === 'add' ? 'Attendance added' : 'Attendance updated');
        setAttDialogOpen(false);
      } else {
        toast.error('Update did not persist — please retry');
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save attendance');
    } finally {
      setAttSaving(false);
    }
  };

  // Get employees who haven't clocked in
  const absentEmployees = useMemo(() => {
    const clockedInEmployees = new Set(dateFilteredAttendance.map(a => a.employee_id));
    return employees.filter(emp => !clockedInEmployees.has(emp.employee_id));
  }, [dateFilteredAttendance, employees]);

  const getStatusBadge = (status) => {
    const styles = {
      early: { bg: 'bg-blue-100 dark:bg-blue-900/30', text: 'text-blue-700 dark:text-blue-300', label: 'Early' },
      on_time: { bg: 'bg-emerald-100 dark:bg-emerald-900/30', text: 'text-emerald-700 dark:text-emerald-300', label: 'On Time' },
      late: { bg: 'bg-amber-100 dark:bg-amber-900/30', text: 'text-amber-700 dark:text-amber-300', label: 'Late' },
      absent: { bg: 'bg-red-100 dark:bg-red-900/30', text: 'text-red-700 dark:text-red-300', label: 'Absent' },
      missing_clock_in: { bg: 'bg-rose-50 dark:bg-rose-900/30', text: 'text-rose-700 dark:text-rose-300', label: 'Missing Clock-In' },
      present: { bg: 'bg-emerald-100 dark:bg-emerald-900/30', text: 'text-emerald-700 dark:text-emerald-300', label: 'Present' },
      half_day: { bg: 'bg-orange-100 dark:bg-orange-900/30', text: 'text-orange-700 dark:text-orange-300', label: 'Half Day' },
      on_leave: { bg: 'bg-sky-100 dark:bg-sky-900/30', text: 'text-sky-700 dark:text-sky-300', label: 'On Leave' },
    };
    return styles[status] || styles.present;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-slate-900 dark:border-slate-100"></div>
      </div>
    );
  }

  // HR Admin View - Analytics and Monitoring
  if (isHRAdmin) {
    return (
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-2xl font-bold text-slate-900 dark:text-slate-100">Attendance Overview</h2>
            <p className="text-slate-600 dark:text-slate-400 mt-1">Monitor employee attendance and punctuality</p>
            <p className="text-xs text-slate-500 dark:text-slate-400 mt-1" data-testid="attendance-payroll-note">
              Attendance is a record-keeping module — late arrivals or missing clock-ins never trigger payroll deductions.
            </p>
          </div>
        </div>

        {/* Date Selector */}
        <div className="bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 p-4">
          <div className="flex items-center space-x-4 flex-wrap gap-y-3">
            <Calendar className="h-5 w-5 text-slate-600 dark:text-slate-400" />
            <label className="text-sm font-semibold text-slate-900 dark:text-slate-100">Select Date:</label>
            <Input
              type="date"
              value={dateFilter}
              onChange={(e) => setDateFilter(e.target.value)}
              className="w-auto dark:bg-slate-900 dark:border-slate-600"
            />
            <span className="text-sm text-slate-600 dark:text-slate-400">
              {new Date(dateFilter).toLocaleDateString('en-MY', { weekday: 'long', day: '2-digit', month: 'long', year: 'numeric' })}
            </span>
            {/* Summary range toggle */}
            <div className="ml-auto flex items-center gap-2" data-testid="attendance-summary-toggle">
              <span className="text-xs text-slate-500 dark:text-slate-400">Summary:</span>
              {['off', 'weekly', 'monthly'].map((opt) => (
                <button
                  key={opt}
                  type="button"
                  onClick={() => setSummaryRange(opt)}
                  data-testid={`summary-${opt}`}
                  className={`px-3 py-1 rounded-full text-xs font-medium transition-colors border ${
                    summaryRange === opt
                      ? 'bg-emerald-600 text-white border-emerald-600'
                      : 'bg-white dark:bg-slate-900 text-slate-600 dark:text-slate-300 border-slate-300 dark:border-slate-600 hover:border-emerald-400'
                  }`}
                >
                  {opt === 'off' ? 'Off' : opt === 'weekly' ? 'Weekly' : 'Monthly'}
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Weekly / Monthly Summary Panel */}
        {summaryData && (
          <div className="bg-gradient-to-r from-emerald-50 to-teal-50 dark:from-emerald-900/20 dark:to-teal-900/20 rounded-xl border border-emerald-200 dark:border-emerald-800 p-5" data-testid="attendance-summary-panel">
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-base font-semibold text-emerald-900 dark:text-emerald-100">
                {summaryRange === 'weekly' ? 'Weekly' : 'Monthly'} Summary
              </h3>
              <span className="text-xs text-emerald-700 dark:text-emerald-300">
                {summaryData.windowStart} → {summaryData.windowEnd}
              </span>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-5 gap-3 text-sm">
              <div>
                <p className="text-emerald-700 dark:text-emerald-300 text-xs">Total Worked</p>
                <p className="text-xl font-bold text-emerald-900 dark:text-emerald-100" data-testid="summary-total-hours">{summaryData.totalWorkedHours}</p>
              </div>
              <div>
                <p className="text-emerald-700 dark:text-emerald-300 text-xs">Days Present</p>
                <p className="text-xl font-bold text-emerald-900 dark:text-emerald-100" data-testid="summary-days-present">{summaryData.daysPresent}</p>
              </div>
              <div>
                <p className="text-amber-700 dark:text-amber-300 text-xs">Late Arrivals</p>
                <p className="text-xl font-bold text-amber-700 dark:text-amber-300" data-testid="summary-late">{summaryData.lateArrivals}</p>
              </div>
              <div>
                <p className="text-rose-700 dark:text-rose-300 text-xs">Early Departures</p>
                <p className="text-xl font-bold text-rose-700 dark:text-rose-300" data-testid="summary-early-departure">{summaryData.earlyDepartures}</p>
              </div>
              <div>
                <p className="text-slate-700 dark:text-slate-300 text-xs">Absences</p>
                <p className="text-xl font-bold text-slate-900 dark:text-slate-100" data-testid="summary-absences">{summaryData.absences}</p>
              </div>
            </div>
          </div>
        )}

        {/* Statistics Cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <button
            onClick={() => setStatusFilter(statusFilter === 'total' ? 'all' : 'total')}
            className={`p-4 rounded-xl border-2 transition-all text-left ${
              statusFilter === 'total' 
                ? 'border-slate-500 bg-slate-50 dark:bg-slate-700' 
                : 'border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 hover:border-slate-300'
            }`}
            data-testid="total-staff-card"
          >
            <div className="flex items-center space-x-2 mb-2">
              <Users className="h-5 w-5 text-slate-600 dark:text-slate-400" />
              <span className="text-sm font-medium text-slate-600 dark:text-slate-400">Total Staff</span>
            </div>
            <p className="text-3xl font-bold text-slate-900 dark:text-slate-100">{attendanceStats.total}</p>
            <p className="text-xs text-blue-500 mt-1">Click to view all staff</p>
          </button>

          <button
            onClick={() => setStatusFilter('early')}
            className={`p-4 rounded-xl border-2 transition-all text-left ${
              statusFilter === 'early' 
                ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20' 
                : 'border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 hover:border-blue-300'
            }`}
          >
            <div className="flex items-center space-x-2 mb-2">
              <TrendingUp className="h-5 w-5 text-blue-600" />
              <span className="text-sm font-medium text-blue-600">Early Birds</span>
            </div>
            <p className="text-3xl font-bold text-blue-600">{attendanceStats.early}</p>
            <p className="text-xs text-slate-500 mt-1">Before 8:30 AM</p>
          </button>

          <button
            onClick={() => setStatusFilter('late')}
            className={`p-4 rounded-xl border-2 transition-all text-left ${
              statusFilter === 'late' 
                ? 'border-amber-500 bg-amber-50 dark:bg-amber-900/20' 
                : 'border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 hover:border-amber-300'
            }`}
          >
            <div className="flex items-center space-x-2 mb-2">
              <AlertTriangle className="h-5 w-5 text-amber-600" />
              <span className="text-sm font-medium text-amber-600">Late Arrivals</span>
            </div>
            <p className="text-3xl font-bold text-amber-600">{attendanceStats.late}</p>
            <p className="text-xs text-slate-500 mt-1">After 9:00 AM</p>
          </button>

          <button
            onClick={() => setStatusFilter('absent')}
            className={`p-4 rounded-xl border-2 transition-all text-left ${
              statusFilter === 'absent' 
                ? 'border-red-500 bg-red-50 dark:bg-red-900/20' 
                : 'border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 hover:border-red-300'
            }`}
          >
            <div className="flex items-center space-x-2 mb-2">
              <XCircle className="h-5 w-5 text-red-600" />
              <span className="text-sm font-medium text-red-600">Not Clocked In</span>
            </div>
            <p className="text-3xl font-bold text-red-600">{attendanceStats.notClocked}</p>
            <p className="text-xs text-slate-500 mt-1">No record</p>
          </button>
        </div>

        {/* Search and Filter */}
        <div className="bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 p-4">
          <div className="flex items-center gap-3 flex-wrap">
            <div className="relative flex-1 min-w-[220px]">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-slate-400" />
              <Input
                placeholder="Search by name, employee ID, or email..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10 dark:bg-slate-900 dark:border-slate-600"
              />
            </div>
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="h-10 rounded-lg border border-slate-300 dark:border-slate-600 px-4 bg-white dark:bg-slate-900 text-slate-900 dark:text-slate-100"
            >
              <option value="all">All Status</option>
              <option value="early">Early Birds</option>
              <option value="on_time">On Time</option>
              <option value="late">Late Arrivals</option>
              <option value="present">Present (marked by HR)</option>
              <option value="missing_clock_in">Missing Clock-In</option>
            </select>
            <select
              value={departmentFilter}
              onChange={(e) => setDepartmentFilter(e.target.value)}
              data-testid="department-filter"
              className="h-10 rounded-lg border border-slate-300 dark:border-slate-600 px-4 bg-white dark:bg-slate-900 text-slate-900 dark:text-slate-100"
            >
              <option value="all">All Departments</option>
              {departmentList.map((d) => (
                <option key={d} value={d}>{d}</option>
              ))}
            </select>
            {/* Monthly report toggle */}
            <button
              type="button"
              onClick={() => setMonthlyReport((v) => !v)}
              data-testid="toggle-monthly-report"
              className={`h-10 px-4 rounded-lg text-sm font-medium border transition-colors ${
                monthlyReport
                  ? 'bg-violet-600 text-white border-violet-600'
                  : 'bg-white dark:bg-slate-900 text-slate-700 dark:text-slate-200 border-slate-300 dark:border-slate-600 hover:border-violet-400'
              }`}
            >
              {monthlyReport ? 'Daily View' : 'Monthly Report'}
            </button>
            {monthlyReport && (
              <Input
                type="month"
                value={reportMonth}
                onChange={(e) => setReportMonth(e.target.value)}
                data-testid="report-month-input"
                className="h-10 w-auto dark:bg-slate-900 dark:border-slate-600"
              />
            )}
            {monthlyReport && (
              <EmployeePicker employees={employees} value={reportEmployeeId} onChange={setReportEmployeeId} />
            )}
            <button
              type="button"
              onClick={exportAttendanceToExcel}
              data-testid="attendance-export-excel"
              className="h-10 px-4 rounded-lg text-sm font-medium bg-emerald-600 hover:bg-emerald-700 text-white"
            >
              Export Excel
            </button>
            {attendanceFiltersActive && (
              <button
                type="button"
                onClick={resetAttendanceFilters}
                data-testid="attendance-reset-filters"
                title="Reset all filters"
                className="h-10 px-3 rounded-lg text-sm font-medium border border-slate-300 dark:border-slate-600 text-slate-700 dark:text-slate-200 bg-white dark:bg-slate-900 hover:border-rose-400 hover:text-rose-600 inline-flex items-center gap-1.5 whitespace-nowrap"
              >
                <RotateCcw className="h-4 w-4" /> Reset
              </button>
            )}
            <button
              type="button"
              onClick={() => openAddAttendance()}
              data-testid="attendance-add-btn"
              className="h-10 px-4 rounded-lg text-sm font-medium bg-violet-600 hover:bg-violet-700 text-white"
            >
              + Add Attendance
            </button>
          </div>
        </div>

        {/* Monthly Attendance Report Table (HR-wide) */}
        {monthlyReport && reportEmployee && (
          <EmployeeMonthlyReport
            employee={reportEmployee}
            month={reportMonth || todayYM}
            records={reportEmployeeRecords}
            approvedLeaves={approvedLeaves}
            publicHolidays={publicHolidays}
            canEdit
            onEditDay={openEditAttendance}
            onMarkPresent={(date) => openAddAttendance(reportEmployee.employee_id, date)}
          />
        )}
        {monthlyReport && !reportEmployee && (
          <div className="bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 overflow-hidden" data-testid="monthly-report-table">
            <div className="p-4 border-b border-slate-200 dark:border-slate-700 flex items-center justify-between">
              <div>
                <h3 className="text-lg font-semibold text-slate-900 dark:text-slate-100">
                  Monthly Attendance Report
                </h3>
                <p className="text-sm text-slate-500 dark:text-slate-400">
                  {new Date(`${reportMonth}-01T00:00:00`).toLocaleDateString('en-MY', { month: 'long', year: 'numeric' })}
                  {' · '}
                  {monthlyReportRows.length} records
                  {departmentFilter !== 'all' && ` · ${departmentFilter}`}
                </p>
              </div>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-slate-50 dark:bg-slate-900 border-b border-slate-200 dark:border-slate-700">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-slate-700 dark:text-slate-300 uppercase">Employee</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-slate-700 dark:text-slate-300 uppercase">Department</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-slate-700 dark:text-slate-300 uppercase">Date</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-slate-700 dark:text-slate-300 uppercase">Clock In</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-slate-700 dark:text-slate-300 uppercase">Clock Out</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-slate-700 dark:text-slate-300 uppercase">Total Hours</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-slate-700 dark:text-slate-300 uppercase">Status</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-slate-700 dark:text-slate-300 uppercase">Flags</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100 dark:divide-slate-700">
                  {monthlyReportRows.length === 0 ? (
                    <tr>
                      <td colSpan="8" className="px-4 py-10 text-center text-sm text-slate-500 dark:text-slate-400">
                        No attendance records for the selected month.
                      </td>
                    </tr>
                  ) : (
                    monthlyReportRows.map((r) => {
                      const emp = employees.find((e) => e.employee_id === r.employee_id);
                      const st = getAttendanceStatus(r);
                      const stStyle = getStatusBadge(st);
                      const earlyOut = isEarlyDeparture(r);
                      return (
                        <tr key={r.attendance_id} className="hover:bg-slate-50 dark:hover:bg-slate-700/40">
                          <td className="px-4 py-3 text-sm text-slate-900 dark:text-slate-100">
                            <div className="font-medium">{emp?.full_name || 'Unknown'}</div>
                            <div className="text-xs text-slate-500">{r.employee_id}</div>
                          </td>
                          <td className="px-4 py-3 text-sm text-slate-700 dark:text-slate-200">{emp?.department || 'Unassigned'}</td>
                          <td className="px-4 py-3 text-sm text-slate-700 dark:text-slate-200">
                            {(r.date || r.clock_in || '').slice(0, 10)}
                          </td>
                          <td className="px-4 py-3 text-sm text-slate-700 dark:text-slate-200">
                            {formatAttendanceTime(r.clock_in, { withMeridiem: true })}
                          </td>
                          <td className="px-4 py-3 text-sm text-slate-700 dark:text-slate-200">
                            {formatAttendanceTime(r.clock_out, { withMeridiem: true })}
                          </td>
                          <td className="px-4 py-3 text-sm font-medium text-slate-800 dark:text-slate-100">
                            {formatWorkedHours(r)}
                          </td>
                          <td className="px-4 py-3">
                            <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${stStyle.bg} ${stStyle.text}`}>{stStyle.label}</span>
                          </td>
                          <td className="px-4 py-3">
                            {earlyOut ? (
                              <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-rose-100 text-rose-700 dark:bg-rose-900/40 dark:text-rose-300">Early Departure</span>
                            ) : <span className="text-xs text-slate-400">—</span>}
                          </td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Attendance Records (Daily view — hidden when Monthly report is active) */}
        {!monthlyReport && (
        <div className="bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 overflow-hidden">
          <div className="p-4 border-b border-slate-200 dark:border-slate-700">
            <h3 className="text-lg font-semibold text-slate-900 dark:text-slate-100">
              {statusFilter === 'all' ? 'All Attendance Records' : 
               statusFilter === 'early' ? 'Early Arrivals' :
               statusFilter === 'late' ? 'Late Arrivals' :
               statusFilter === 'absent' ? 'Not Clocked In' : 
               statusFilter === 'total' ? 'All Staff List' : 'Attendance Records'}
            </h3>
            <p className="text-sm text-slate-500 dark:text-slate-400">
              {statusFilter === 'absent' ? absentEmployees.length : 
               statusFilter === 'total' ? employees.length : filteredAttendance.length} records found
            </p>
          </div>

          {/* Show all employees when Total Staff is clicked */}
          {statusFilter === 'total' ? (
            <div className="overflow-x-auto">
              {employees.length === 0 ? (
                <div className="text-center py-12">
                  <Users className="h-12 w-12 text-slate-300 dark:text-slate-600 mx-auto mb-3" />
                  <p className="text-slate-500 dark:text-slate-400">No employees found</p>
                </div>
              ) : (
                <table className="w-full">
                  <thead className="bg-slate-50 dark:bg-slate-900 border-b border-slate-200 dark:border-slate-700">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-semibold text-slate-700 dark:text-slate-300 uppercase">Employee</th>
                      <th className="px-6 py-3 text-left text-xs font-semibold text-slate-700 dark:text-slate-300 uppercase">Department</th>
                      <th className="px-6 py-3 text-left text-xs font-semibold text-slate-700 dark:text-slate-300 uppercase">Position</th>
                      <th className="px-6 py-3 text-left text-xs font-semibold text-slate-700 dark:text-slate-300 uppercase">Status</th>
                      <th className="px-6 py-3 text-left text-xs font-semibold text-slate-700 dark:text-slate-300 uppercase">Attendance Today</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100 dark:divide-slate-700">
                    {employees
                      .filter(emp => {
                        if (!searchQuery) return true;
                        const query = searchQuery.toLowerCase();
                        return emp.full_name?.toLowerCase().includes(query) ||
                               emp.employee_id?.toLowerCase().includes(query) ||
                               emp.email?.toLowerCase().includes(query);
                      })
                      .map((emp) => {
                        const todayRecord = dateFilteredAttendance.find(a => a.employee_id === emp.employee_id);
                        const hasClockedIn = !!todayRecord?.clock_in;
                        return (
                        <tr key={emp.employee_id} className="hover:bg-slate-50 dark:hover:bg-slate-700">
                          <td className="px-6 py-4">
                            <div className="flex items-center space-x-3">
                              <div className={`h-10 w-10 rounded-full flex items-center justify-center ${
                                emp.status === 'terminated' || emp.status === 'resigned'
                                  ? 'bg-gradient-to-br from-slate-400 to-slate-500'
                                  : 'bg-gradient-to-br from-emerald-400 to-teal-500'
                              }`}>
                                <span className="text-white font-semibold text-sm">
                                  {emp.full_name?.charAt(0) || 'U'}
                                </span>
                              </div>
                              <div>
                                <p className="font-medium text-slate-900 dark:text-slate-100">{emp.full_name || 'Unknown'}</p>
                                <p className="text-xs text-slate-500 dark:text-slate-400">{emp.employee_id}</p>
                                <p className="text-xs text-slate-400">{emp.email}</p>
                              </div>
                            </div>
                          </td>
                          <td className="px-6 py-4 text-sm text-slate-700 dark:text-slate-300">
                            {emp.department || 'N/A'}
                          </td>
                          <td className="px-6 py-4 text-sm text-slate-700 dark:text-slate-300">
                            {emp.position || 'N/A'}
                          </td>
                          <td className="px-6 py-4">
                            {/* Iter132 — Show on_notice_period as a
                                distinct amber pill so HR can tell the
                                difference between "still serving notice"
                                and "fully terminated". Reported by Ankur
                                19-Jun-2026 (savin vs Ahmad bin Ibrahim
                                were indistinguishable on this page). */}
                            {(() => {
                              const s = emp.status;
                              if (s === 'on_notice_period') {
                                return (
                                  <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-300">
                                    On Notice
                                  </span>
                                );
                              }
                              const isOff = s === 'terminated' || s === 'resigned' || s === 'inactive';
                              return (
                                <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium ${
                                  isOff
                                    ? 'bg-slate-100 dark:bg-slate-900 text-slate-600 dark:text-slate-400'
                                    : 'bg-emerald-100 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-300'
                                }`}>
                                  {s || 'Active'}
                                </span>
                              );
                            })()}
                          </td>
                          <td className="px-6 py-4">
                            {hasClockedIn ? (
                              <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-emerald-100 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-300">
                                <CheckCircle className="h-3 w-3 mr-1" />
                                Clocked In
                              </span>
                            ) : (
                              <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-300">
                                <XCircle className="h-3 w-3 mr-1" />
                                Not Clocked In
                              </span>
                            )}
                          </td>
                        </tr>
                      );})}
                  </tbody>
                </table>
              )}
            </div>
          ) : statusFilter === 'absent' ? (
            <div className="overflow-x-auto">
              {absentEmployees.length === 0 ? (
                <div className="text-center py-12">
                  <CheckCircle className="h-12 w-12 text-emerald-300 mx-auto mb-3" />
                  <p className="text-slate-500 dark:text-slate-400">All employees have clocked in today!</p>
                </div>
              ) : (
                <table className="w-full">
                  <thead className="bg-slate-50 dark:bg-slate-900 border-b border-slate-200 dark:border-slate-700">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-semibold text-slate-700 dark:text-slate-300 uppercase">Employee</th>
                      <th className="px-6 py-3 text-left text-xs font-semibold text-slate-700 dark:text-slate-300 uppercase">Department</th>
                      <th className="px-6 py-3 text-left text-xs font-semibold text-slate-700 dark:text-slate-300 uppercase">Position</th>
                      <th className="px-6 py-3 text-left text-xs font-semibold text-slate-700 dark:text-slate-300 uppercase">Status</th>
                      <th className="px-6 py-3 text-left text-xs font-semibold text-slate-700 dark:text-slate-300 uppercase">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100 dark:divide-slate-700">
                    {absentEmployees
                      .filter(emp => {
                        if (!searchQuery) return true;
                        const query = searchQuery.toLowerCase();
                        return emp.full_name?.toLowerCase().includes(query) ||
                               emp.employee_id?.toLowerCase().includes(query) ||
                               emp.email?.toLowerCase().includes(query);
                      })
                      .map((emp) => (
                        <tr key={emp.employee_id} className="hover:bg-slate-50 dark:hover:bg-slate-700">
                          <td className="px-6 py-4">
                            <div className="flex items-center space-x-3">
                              <div className="h-10 w-10 rounded-full bg-gradient-to-br from-red-400 to-rose-500 flex items-center justify-center">
                                <span className="text-white font-semibold text-sm">
                                  {emp.full_name?.charAt(0) || 'U'}
                                </span>
                              </div>
                              <div>
                                <p className="font-medium text-slate-900 dark:text-slate-100">{emp.full_name || 'Unknown'}</p>
                                <p className="text-xs text-slate-500 dark:text-slate-400">{emp.employee_id}</p>
                                <p className="text-xs text-slate-400">{emp.email}</p>
                              </div>
                            </div>
                          </td>
                          <td className="px-6 py-4 text-sm text-slate-700 dark:text-slate-300">
                            {emp.department || 'N/A'}
                          </td>
                          <td className="px-6 py-4 text-sm text-slate-700 dark:text-slate-300">
                            {emp.position || 'N/A'}
                          </td>
                          <td className="px-6 py-4">
                            <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-300">
                              Not Clocked In
                            </span>
                          </td>
                          <td className="px-6 py-4">
                            <button
                              type="button"
                              onClick={() => openAddAttendance(emp.employee_id)}
                              data-testid={`mark-present-${emp.employee_id}`}
                              className="px-3 py-1 rounded-lg text-xs font-medium border border-violet-300 text-violet-700 hover:bg-violet-50 dark:hover:bg-violet-900/30"
                            >
                              Mark Present
                            </button>
                          </td>
                        </tr>
                      ))}
                  </tbody>
                </table>
              )}
            </div>
          ) : (
            <div className="overflow-x-auto">
              {filteredAttendance.length === 0 ? (
                <div className="text-center py-12">
                  <Calendar className="h-12 w-12 text-slate-300 dark:text-slate-600 mx-auto mb-3" />
                  <p className="text-slate-500 dark:text-slate-400">
                    {searchQuery ? 'No employees found matching your search' : 'No attendance records found for this filter'}
                  </p>
                </div>
              ) : (
                <table className="w-full">
                  <thead className="bg-slate-50 dark:bg-slate-900 border-b border-slate-200 dark:border-slate-700">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-semibold text-slate-700 dark:text-slate-300 uppercase">Employee</th>
                      <th className="px-6 py-3 text-left text-xs font-semibold text-slate-700 dark:text-slate-300 uppercase">Clock In</th>
                      <th className="px-6 py-3 text-left text-xs font-semibold text-slate-700 dark:text-slate-300 uppercase">Clock Out</th>
                      <th className="px-6 py-3 text-left text-xs font-semibold text-slate-700 dark:text-slate-300 uppercase">Working Hours</th>
                      <th className="px-6 py-3 text-left text-xs font-semibold text-slate-700 dark:text-slate-300 uppercase">Status</th>
                      <th className="px-6 py-3 text-left text-xs font-semibold text-slate-700 dark:text-slate-300 uppercase">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100 dark:divide-slate-700">
                    {filteredAttendance.map((record) => {
                      const employee = employees.find(e => e.employee_id === record.employee_id);
                      const status = getAttendanceStatus(record);
                      const statusStyle = getStatusBadge(status);
                      const workingHours = formatWorkedHours(record);
                      const earlyOut = isEarlyDeparture(record);
                      const dateKey = (record.date || '').slice(0, 10);
                      const onLeaveRow = isOnLeaveOnDate(record.employee_id, dateKey, approvedLeaves);
                      const holidayRow = findPublicHoliday(dateKey, publicHolidays);
                      // Manual edits/overrides take precedence over the
                      // auto-detected On-Leave / Public-Holiday badges.
                      const manualOverride = hasManualStatusOverride(record);
                      // Iter88m — If the attendance row itself carries
                      // `auto_resolved_leave`, we DO NOT show the "On Leave"
                      // badge — the leave for this day was auto-cancelled
                      // because the employee physically attended. Show a
                      // small informational pill instead.
                      const autoResolvedLeave = record.auto_resolved_leave;
                      const showHoliday = holidayRow && !manualOverride && !autoResolvedLeave;
                      const showOnLeave = onLeaveRow && !manualOverride && !autoResolvedLeave;
                      
                      return (
                        <tr 
                          key={record.attendance_id} 
                          className="hover:bg-slate-50 dark:hover:bg-slate-700 transition-colors"
                        >
                          <td className="px-6 py-4">
                            <div className="flex items-center space-x-3">
                              <div className={`h-10 w-10 rounded-full flex items-center justify-center ${
                                status === 'early' ? 'bg-gradient-to-br from-blue-400 to-indigo-500' :
                                status === 'late' ? 'bg-gradient-to-br from-amber-400 to-orange-500' :
                                'bg-gradient-to-br from-emerald-400 to-teal-500'
                              }`}>
                                <span className="text-white font-semibold text-sm">
                                  {employee?.full_name?.charAt(0) || 'U'}
                                </span>
                              </div>
                              <div>
                                <p className="font-medium text-slate-900 dark:text-slate-100">{employee?.full_name || 'Unknown'}</p>
                                <p className="text-xs text-slate-500 dark:text-slate-400">{record.employee_id}</p>
                                <p className="text-xs text-slate-400">{employee?.email}</p>
                              </div>
                            </div>
                          </td>
                          <td className="px-6 py-4">
                            <div className="flex items-center space-x-2">
                              <LogIn className="h-4 w-4 text-emerald-500" />
                              <span className="text-sm font-medium text-slate-900 dark:text-slate-100">
                                {formatAttendanceTime(record.clock_in, { withMeridiem: true })}
                              </span>
                            </div>
                          </td>
                          <td className="px-6 py-4">
                            <div className="flex items-center space-x-2">
                              <LogOut className="h-4 w-4 text-red-500" />
                              <span className="text-sm font-medium text-slate-900 dark:text-slate-100">
                                {formatAttendanceTime(record.clock_out, { withMeridiem: true })}
                              </span>
                            </div>
                          </td>
                          <td className="px-6 py-4">
                            <span className="text-sm font-medium text-slate-700 dark:text-slate-300">
                              {workingHours}
                            </span>
                          </td>
                          <td className="px-6 py-4">
                            <div className="flex flex-col gap-1">
                              {showHoliday ? (
                                <span data-testid={`holiday-${record.attendance_id}`} className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-indigo-100 text-indigo-700 dark:bg-indigo-900/40 dark:text-indigo-300 w-fit" title={holidayRow.name}>
                                  Public Holiday
                                </span>
                              ) : showOnLeave ? (
                                <span data-testid={`on-leave-${record.attendance_id}`} className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-sky-100 text-sky-700 dark:bg-sky-900/40 dark:text-sky-300 w-fit">
                                  On Leave
                                </span>
                              ) : (
                                <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium ${statusStyle.bg} ${statusStyle.text}`}>
                                  {statusStyle.label}
                                </span>
                              )}
                              <HrMarkedPill record={record} />
                              {earlyOut && !showOnLeave && !showHoliday && (
                                <span data-testid={`early-departure-${record.attendance_id}`} className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-rose-100 text-rose-700 dark:bg-rose-900/40 dark:text-rose-300 w-fit">
                                  Early Departure
                                </span>
                              )}
                              {/* Iter88m — Show "Was on leave (auto-cancelled)" badge.
                                  Iter53 (Bug 4) — Hide this pill when HR has
                                  manually overridden the status; otherwise the
                                  row showed contradictory chips like
                                  "Present + Was on leave auto-cancelled". */}
                              {autoResolvedLeave && !manualOverride && (
                                <span
                                  data-testid={`auto-resolved-leave-${record.attendance_id}`}
                                  className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300 w-fit"
                                  title={`Approved leave for this day was auto-cancelled because the employee was physically present. ${(autoResolvedLeave.days_refunded || 0)} day(s) refunded to ${autoResolvedLeave.balance_field || 'balance'}.`}
                                >
                                  Was on leave · auto-cancelled
                                </span>
                              )}
                            </div>
                          </td>
                          <td className="px-6 py-4">
                            <div className="flex items-center gap-2">
                              <button
                                type="button"
                                onClick={() => openEditAttendance(record)}
                                data-testid={`edit-attendance-${record.attendance_id}`}
                                className="px-3 py-1 rounded-lg text-xs font-medium border border-violet-300 text-violet-700 hover:bg-violet-50 dark:hover:bg-violet-900/30"
                              >
                                Edit
                              </button>
                              <button
                                type="button"
                                onClick={() => openEmployeeMonthlyReport(record.employee_id)}
                                data-testid={`monthly-report-${record.attendance_id}`}
                                title="View this employee's monthly attendance report"
                                className="px-3 py-1 rounded-lg text-xs font-medium border border-slate-300 dark:border-slate-600 text-slate-700 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-700"
                              >
                                Monthly
                              </button>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              )}
            </div>
          )}
        </div>
        )}

        {/* Add / Edit Attendance Dialog (HR only) */}
        <Dialog open={attDialogOpen} onOpenChange={setAttDialogOpen}>
          <DialogContent className="max-w-md bg-white dark:bg-slate-800" style={{ borderRadius: '20px' }}>
            <DialogHeader>
              <DialogTitle className="flex items-center text-slate-900 dark:text-slate-100">
                {attDialogMode === 'add' ? <Plus className="h-5 w-5 mr-2 text-violet-500" /> : <Pencil className="h-5 w-5 mr-2 text-violet-500" />}
                {attDialogMode === 'add' ? 'Add Attendance' : 'Edit Attendance'}
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-3 mt-2">
              <div>
                <Label>Employee</Label>
                <select
                  data-testid="att-form-employee"
                  value={attForm.employee_id}
                  onChange={(e) => setAttForm({ ...attForm, employee_id: e.target.value })}
                  disabled={attDialogMode === 'edit'}
                  className="w-full mt-1 h-10 px-3 border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-700 text-slate-900 dark:text-slate-100"
                  style={{ borderRadius: '12px' }}
                >
                  {employees.map((emp) => (
                    <option key={emp.employee_id} value={emp.employee_id}>{emp.full_name} ({emp.employee_id})</option>
                  ))}
                </select>
              </div>
              <div>
                <Label>Date</Label>
                <Input
                  data-testid="att-form-date"
                  type="date"
                  value={attForm.date}
                  /* Iter88k — Block future-dated attendance entry from the UI.
                     The backend also rejects, but a `max` attribute disables
                     future dates in the native picker so HR can't even pick one. */
                  max={new Date(Date.now() + 8 * 3600 * 1000).toISOString().slice(0, 10)}
                  onChange={(e) => setAttForm({ ...attForm, date: e.target.value })}
                  className="mt-1"
                  style={{ borderRadius: '12px' }}
                />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Clock In (HH:MM)</Label>
                  <Input
                    data-testid="att-form-clock-in"
                    type="time"
                    value={attForm.clock_in}
                    onChange={(e) => setAttForm({ ...attForm, clock_in: e.target.value })}
                    className="mt-1"
                    style={{ borderRadius: '12px' }}
                  />
                </div>
                <div>
                  <Label>Clock Out (HH:MM)</Label>
                  <Input
                    data-testid="att-form-clock-out"
                    type="time"
                    value={attForm.clock_out}
                    onChange={(e) => setAttForm({ ...attForm, clock_out: e.target.value })}
                    className="mt-1"
                    style={{ borderRadius: '12px' }}
                  />
                </div>
              </div>
              <div>
                <Label>Status</Label>
                <select
                  data-testid="att-form-status"
                  value={attForm.status}
                  onChange={(e) => setAttForm({ ...attForm, status: e.target.value })}
                  className="w-full mt-1 h-10 px-3 border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-700 text-slate-900 dark:text-slate-100"
                  style={{ borderRadius: '12px' }}
                >
                  <option value="present">Present</option>
                  <option value="absent">Absent</option>
                  <option value="late">Late</option>
                  <option value="half_day">Half Day</option>
                  <option value="on_leave">On Leave</option>
                </select>
              </div>
              {isAttendedStatus(attForm.status) && (
                <div>
                  <Label>
                    Reason (marked by HR){needsPresenceReason ? <span className="text-rose-600"> *</span> : ' — optional'}
                  </Label>
                  <select
                    data-testid="att-form-presence-reason"
                    value={attForm.presence_reason}
                    onChange={(e) => setAttForm({ ...attForm, presence_reason: e.target.value })}
                    className="w-full mt-1 h-10 px-3 border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-700 text-slate-900 dark:text-slate-100"
                    style={{ borderRadius: '12px' }}
                  >
                    <option value="">Select a reason…</option>
                    {PRESENCE_REASONS.map((r) => (
                      <option key={r.value} value={r.value}>{r.label}</option>
                    ))}
                  </select>
                  <p className="text-xs text-slate-500 dark:text-slate-400 mt-1" data-testid="att-form-presence-hint">
                    {needsPresenceReason
                      ? `No clock-in time entered — state why the employee is recorded as ${attForm.status === 'half_day' ? 'Half Day' : attForm.status === 'late' ? 'Late' : 'Present'} (e.g. attending a client meeting). Clock-in/out can stay blank.`
                      : 'Optional — note why HR is recording this attendance on the employee\'s behalf.'}
                  </p>
                </div>
              )}
              {/* Iter90 — Show the "This is a rest day" opt-in checkbox
                  only when the selected date is a Sunday. Prevents
                  Sunday attendance from being silently accepted while
                  still giving HR the escape hatch for legitimate
                  rest-day work. Backend rejects with a clear message if
                  this flag is missing. */}
              {attForm.date && new Date(attForm.date + 'T00:00:00').getDay() === 0 && (
                <div className="p-3 rounded-lg border border-amber-300 dark:border-amber-800 bg-amber-50 dark:bg-amber-900/20">
                  <label className="flex items-start gap-2 cursor-pointer text-sm">
                    <input
                      type="checkbox"
                      checked={Boolean(attForm.allow_rest_day)}
                      onChange={(e) => setAttForm({ ...attForm, allow_rest_day: e.target.checked })}
                      className="mt-1"
                      data-testid="att-form-allow-rest-day"
                    />
                    <span className="text-amber-800 dark:text-amber-300">
                      <strong>{attForm.date} is a Sunday (weekly rest day).</strong>{' '}
                      Tick this box to confirm the employee genuinely worked on their
                      rest day. Please also submit a{' '}
                      <strong>Rest-Day overtime request</strong> so they receive the
                      2x multiplier per the Malaysia Employment Act.
                    </span>
                  </label>
                </div>
              )}
              <div>
                <Label>Notes</Label>
                <Input
                  data-testid="att-form-notes"
                  value={attForm.notes}
                  onChange={(e) => setAttForm({ ...attForm, notes: e.target.value })}
                  placeholder="Optional notes"
                  className="mt-1"
                  style={{ borderRadius: '12px' }}
                />
              </div>
              {/* Iter92 (Shikha 10-Jul-2026) — Client-side guard: block
                  Save when clock-out is not AFTER clock-in on the same
                  date. Same rule as the backend Iter91 guard, surfaced
                  in the dialog so HR sees the problem immediately
                  instead of after a round-trip 400. Overnight-shift
                  workaround (log two rows) is mentioned in the hint. */}
              {(() => {
                const ci = (attForm.clock_in || '').trim();
                const co = (attForm.clock_out || '').trim();
                if (!ci || !co) return null;
                const bad = co <= ci;
                if (!bad) return null;
                return (
                  <div className="p-3 rounded-lg border border-red-300 dark:border-red-800 bg-red-50 dark:bg-red-900/20 text-sm text-red-800 dark:text-red-300" data-testid="att-form-clock-order-error">
                    <strong>Clock-out must be after clock-in.</strong> You entered
                    clock-in <code>{ci}</code> and clock-out <code>{co}</code>. For an
                    overnight shift ending the next day, log the clock-out on the
                    following day&apos;s attendance record instead of the same day.
                  </div>
                );
              })()}
              <div className="flex justify-end space-x-2 pt-2">
                <Button type="button" variant="outline" onClick={() => setAttDialogOpen(false)} style={{ borderRadius: '12px' }}>
                  Cancel
                </Button>
                <Button
                  type="button"
                  data-testid="att-form-save"
                  onClick={submitAttendanceDialog}
                  disabled={
                    attSaving ||
                    (needsPresenceReason && !attForm.presence_reason) ||
                    (Boolean((attForm.clock_in || '').trim()) &&
                      Boolean((attForm.clock_out || '').trim()) &&
                      (attForm.clock_out || '').trim() <= (attForm.clock_in || '').trim())
                  }
                  className="bg-violet-600 hover:bg-violet-700"
                  style={{ borderRadius: '12px' }}
                >
                  {attSaving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <CheckCircle className="h-4 w-4 mr-2" />}
                  {attDialogMode === 'add' ? 'Add' : 'Save Changes'}
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    );
  }

  // Employee/Manager View (Original)
  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-bold text-slate-900 dark:text-slate-100">Attendance Tracking</h2>
        <p className="text-slate-600 dark:text-slate-400 mt-1">
          {isEmployee 
            ? 'Your attendance records' 
            : isManager 
              ? `Team attendance records (${employees.length} team members)` 
              : `${attendance.length} total records`}
        </p>
      </div>

      {/* Clock In/Out Section - For employees (self only) or HR (select employee).
          Iter84 — Hidden for managers since they get their own dedicated
          "My Clock In / Out" section below with a clearer hint about
          self-only clock-in. Showing both was confusing testers — they
          saw two emerald cards with the same buttons. */}
      {/* Iter85 — Pre-boarding banner for employees not yet activated. */}
      {isPreboarding && (
        <div
          className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-xl p-5 flex items-start gap-3"
          data-testid="preboarding-attendance-banner"
        >
          <Clock className="h-5 w-5 mt-0.5 text-amber-600 dark:text-amber-300 flex-shrink-0" />
          <div>
            <p className="font-semibold text-amber-800 dark:text-amber-200">Attendance not yet active</p>
            <p className="text-sm text-amber-700 dark:text-amber-300/90 mt-1">
              You are currently in <strong>Pre-boarding</strong> status. Clock-in/out becomes
              available once HR activates your employment on your join date.
            </p>
          </div>
        </div>
      )}

      {/* Iter55 (Ankur Bug 2) — Hide the Clock In/Out card for users whose
          account has no employee profile (e.g. Super Admin, some legacy
          payroll-officer accounts). They can't clock attendance against
          themselves, and showing the card just produced the confusing
          "Already clocked in today" error. Use the same gate inside the
          "My Clock In/Out" section below for consistency. */}
      {!isManager && !isPreboarding && user.employee_id && (
      <div className="bg-gradient-to-br from-emerald-500 to-teal-600 rounded-2xl p-8 text-white">
        <div className="flex items-center justify-between mb-6 flex-wrap gap-2">
          <div className="flex items-center space-x-3">
            <Clock className="h-8 w-8" />
            <h3 className="text-2xl font-bold">Clock In / Out</h3>
          </div>
          {/* Iter85 — Make the operational timezone explicit so testers in
              IST/PST/other zones understand the displayed time is Malaysia
              office time, not their browser-local time. */}
          <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-white/15 backdrop-blur-sm" title="All clock-in/out times are recorded in Malaysia Time (UTC+8)">
            Times in MYT (UTC+8)
          </span>
        </div>
        <div className="flex items-end gap-4 flex-wrap">
          {/* Employee selector - Only show for HR Admin, hide for employees */}
          {isHRAdmin && (
            <div className="flex-1 min-w-[180px]">
              <label className="block text-sm font-medium mb-2">Select Employee</label>
              <select
                value={selectedEmployee}
                onChange={(e) => setSelectedEmployee(e.target.value)}
                data-testid="employee-select"
                className="w-full h-11 rounded-xl border-0 px-4 text-slate-900"
              >
                {employees.map(emp => (
                  <option key={emp.employee_id} value={emp.employee_id}>
                    {emp.full_name} ({emp.employee_id})
                  </option>
                ))}
              </select>
            </div>
          )}
          {/* Show current employee name for regular employees */}
          {(isEmployee || isManager) && (
            <div className="flex-1 min-w-[180px]">
              <label className="block text-sm font-medium mb-2">Employee</label>
              <div className="w-full h-11 rounded-xl bg-white/20 px-4 flex items-center text-white font-medium">
                {user.full_name}
              </div>
            </div>
          )}
          <Button
            onClick={handleClockIn}
            data-testid="clock-in-btn"
            className="h-11 bg-white text-emerald-600 hover:bg-emerald-50"
          >
            <LogIn className="h-4 w-4 mr-2" />
            Clock In
          </Button>
          <Button
            onClick={handleClockOut}
            data-testid="clock-out-btn"
            className="h-11 bg-slate-900 text-white hover:bg-slate-800"
          >
            <LogOut className="h-4 w-4 mr-2" />
            Clock Out
          </Button>
        </div>
      </div>
      )}
      
      {/* Manager Clock In/Out - Only for Self */}
      {isManager && (
        <div className="bg-gradient-to-br from-emerald-500 to-teal-600 rounded-2xl p-8 text-white">
          <div className="flex items-center justify-between mb-6 flex-wrap gap-2">
            <div className="flex items-center space-x-3">
              <Clock className="h-8 w-8" />
              <h3 className="text-2xl font-bold">My Clock In / Out</h3>
            </div>
            {/* Iter85 — MYT timezone label, same as the employee card. */}
            <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-white/15 backdrop-blur-sm" title="All clock-in/out times are recorded in Malaysia Time (UTC+8)">
              Times in MYT (UTC+8)
            </span>
          </div>
          <div className="flex items-end gap-4 flex-wrap">
            <div className="flex-1 min-w-[180px]">
              <label className="block text-sm font-medium mb-2">Your Attendance</label>
              <div className="w-full h-11 rounded-xl bg-white px-4 text-slate-900 flex items-center font-medium">
                {user.full_name} ({user.employee_id})
              </div>
            </div>
            <Button
              onClick={handleClockIn}
              data-testid="clock-in-btn"
              className="h-11 bg-white text-emerald-600 hover:bg-emerald-50"
            >
              <LogIn className="h-4 w-4 mr-2" />
              Clock In
            </Button>
            <Button
              onClick={handleClockOut}
              data-testid="clock-out-btn"
              className="h-11 bg-slate-900 text-white hover:bg-slate-800"
            >
              <LogOut className="h-4 w-4 mr-2" />
              Clock Out
            </Button>
          </div>
          <p className="text-emerald-100 text-sm mt-4">
            💡 You can only clock in/out for yourself. View your team's attendance below.
          </p>
        </div>
      )}

      {/* Date Filter for Managers */}
      {isManager && (
        <div className="bg-white dark:bg-slate-800 rounded-xl border border-slate-100 dark:border-slate-700 p-6">
          <div className="flex items-center space-x-4">
            <Calendar className="h-5 w-5 text-slate-600 dark:text-slate-400" />
            <label className="text-sm font-semibold text-slate-900 dark:text-slate-100">Filter by Date:</label>
            <Input
              type="date"
              value={dateFilter}
              onChange={(e) => setDateFilter(e.target.value)}
              className="w-auto"
            />
            <span className="text-sm text-slate-600 dark:text-slate-400">
              Showing {dateFilteredAttendance.length} record(s) for {new Date(dateFilter).toLocaleDateString('en-MY', { day: '2-digit', month: 'short', year: 'numeric' })}
            </span>
          </div>
        </div>
      )}

      {/* Summary toggle for Employee/Manager view */}
      <div className="bg-white dark:bg-slate-800 rounded-xl border border-slate-100 dark:border-slate-700 p-4 flex items-center justify-between flex-wrap gap-y-3">
        <span className="text-sm font-semibold text-slate-900 dark:text-slate-100">My Attendance Summary</span>
        <div className="flex items-center gap-2 flex-wrap" data-testid="attendance-summary-toggle-emp">
          <span className="text-xs text-slate-500 dark:text-slate-400">Range:</span>
          {['off', 'weekly', 'monthly'].map((opt) => (
            <button
              key={opt}
              type="button"
              onClick={() => setSummaryRange(opt)}
              data-testid={`summary-emp-${opt}`}
              className={`px-3 py-1 rounded-full text-xs font-medium transition-colors border ${
                summaryRange === opt
                  ? 'bg-emerald-600 text-white border-emerald-600'
                  : 'bg-white dark:bg-slate-900 text-slate-600 dark:text-slate-300 border-slate-300 dark:border-slate-600 hover:border-emerald-400'
              }`}
            >
              {opt === 'off' ? 'Off' : opt === 'weekly' ? 'Weekly' : 'Monthly'}
            </button>
          ))}
          <span className="mx-2 h-5 w-px bg-slate-300 dark:bg-slate-600" />
          <button
            type="button"
            onClick={() => setMonthlyReport((v) => !v)}
            data-testid="toggle-monthly-report-emp"
            className={`px-3 py-1 rounded-full text-xs font-medium transition-colors border ${
              monthlyReport
                ? 'bg-violet-600 text-white border-violet-600'
                : 'bg-white dark:bg-slate-900 text-slate-600 dark:text-slate-300 border-slate-300 dark:border-slate-600 hover:border-violet-400'
            }`}
          >
            {monthlyReport ? 'Hide Monthly Report' : 'Monthly Report'}
          </button>
          {monthlyReport && (
            <Input
              type="month"
              value={reportMonth}
              onChange={(e) => setReportMonth(e.target.value)}
              data-testid="report-month-input-emp"
              className="h-8 w-auto dark:bg-slate-900 dark:border-slate-600 text-xs"
            />
          )}
          {monthlyReport && isManager && (
            <EmployeePicker employees={employees} value={reportEmployeeId} onChange={setReportEmployeeId} placeholder="Search team member…" />
          )}
          <button
            type="button"
            onClick={exportAttendanceToExcel}
            data-testid="attendance-export-excel-emp"
            className="h-8 px-3 rounded-full text-xs font-medium bg-emerald-600 hover:bg-emerald-700 text-white"
          >
            Export Excel
          </button>
        </div>
      </div>

      {summaryData && (
        <div className="bg-gradient-to-r from-emerald-50 to-teal-50 dark:from-emerald-900/20 dark:to-teal-900/20 rounded-xl border border-emerald-200 dark:border-emerald-800 p-5" data-testid="attendance-summary-panel-emp">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-base font-semibold text-emerald-900 dark:text-emerald-100">
              {summaryRange === 'weekly' ? 'Weekly' : 'Monthly'} Summary
            </h3>
            <span className="text-xs text-emerald-700 dark:text-emerald-300">
              {summaryData.windowStart} → {summaryData.windowEnd}
            </span>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3 text-sm">
            <div>
              <p className="text-emerald-700 dark:text-emerald-300 text-xs">Total Worked</p>
              <p className="text-xl font-bold text-emerald-900 dark:text-emerald-100">{summaryData.totalWorkedHours}</p>
            </div>
            <div>
              <p className="text-emerald-700 dark:text-emerald-300 text-xs">Days Present</p>
              <p className="text-xl font-bold text-emerald-900 dark:text-emerald-100">{summaryData.daysPresent}</p>
            </div>
            <div>
              <p className="text-amber-700 dark:text-amber-300 text-xs">Late Arrivals</p>
              <p className="text-xl font-bold text-amber-700 dark:text-amber-300">{summaryData.lateArrivals}</p>
            </div>
            <div>
              <p className="text-rose-700 dark:text-rose-300 text-xs">Early Departures</p>
              <p className="text-xl font-bold text-rose-700 dark:text-rose-300">{summaryData.earlyDepartures}</p>
            </div>
            {!isEmployee && (
              <div>
                <p className="text-slate-700 dark:text-slate-300 text-xs">Absences</p>
                <p className="text-xl font-bold text-slate-900 dark:text-slate-100">{summaryData.absences}</p>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Monthly Attendance Report (Employee/Manager) */}
      {monthlyReport && reportEmployee && (
        <EmployeeMonthlyReport
          employee={reportEmployee}
          month={reportMonth || todayYM}
          records={reportEmployeeRecords}
          approvedLeaves={approvedLeaves}
          publicHolidays={publicHolidays}
        />
      )}
      {monthlyReport && !reportEmployee && (
        <div className="bg-white dark:bg-slate-800 rounded-xl border border-slate-100 dark:border-slate-700 overflow-hidden" data-testid="monthly-report-table-emp">
          <div className="p-4 border-b border-slate-100 dark:border-slate-700 flex items-center justify-between flex-wrap gap-y-2">
            <div>
              <h3 className="text-base font-semibold text-slate-900 dark:text-slate-100">Monthly Attendance Report</h3>
              <p className="text-xs text-slate-500 dark:text-slate-400">
                {new Date(`${reportMonth}-01T00:00:00`).toLocaleDateString('en-MY', { month: 'long', year: 'numeric' })}
                {' · '}{monthlyReportRows.length} records
              </p>
            </div>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-slate-50 dark:bg-slate-900 border-b border-slate-200 dark:border-slate-700">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-slate-700 dark:text-slate-300 uppercase">Date</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-slate-700 dark:text-slate-300 uppercase">Employee</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-slate-700 dark:text-slate-300 uppercase">Department</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-slate-700 dark:text-slate-300 uppercase">Clock In</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-slate-700 dark:text-slate-300 uppercase">Clock Out</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-slate-700 dark:text-slate-300 uppercase">Total Hours</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-slate-700 dark:text-slate-300 uppercase">Status</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-slate-700 dark:text-slate-300 uppercase">Flags</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100 dark:divide-slate-700">
                {monthlyReportRows.length === 0 ? (
                  <tr><td colSpan="8" className="px-4 py-10 text-center text-sm text-slate-500 dark:text-slate-400">No attendance records for the selected month.</td></tr>
                ) : monthlyReportRows.map((r) => {
                  const emp = employees.find((e) => e.employee_id === r.employee_id);
                  const earlyOut = isEarlyDeparture(r);
                  return (
                    <tr key={r.attendance_id} className="hover:bg-slate-50 dark:hover:bg-slate-700/40">
                      <td className="px-4 py-3 text-sm text-slate-700 dark:text-slate-200">{(r.date || r.clock_in || '').slice(0, 10)}</td>
                      <td className="px-4 py-3 text-sm text-slate-900 dark:text-slate-100">
                        <div className="font-medium">{emp?.full_name || 'Unknown'}</div>
                        <div className="text-xs text-slate-500">{r.employee_id}</div>
                      </td>
                      <td className="px-4 py-3 text-sm text-slate-700 dark:text-slate-200">{emp?.department || 'Unassigned'}</td>
                      <td className="px-4 py-3 text-sm text-slate-700 dark:text-slate-200">{formatAttendanceTime(r.clock_in, { withMeridiem: true })}</td>
                      <td className="px-4 py-3 text-sm text-slate-700 dark:text-slate-200">{formatAttendanceTime(r.clock_out, { withMeridiem: true })}</td>
                      <td className="px-4 py-3 text-sm font-medium text-slate-800 dark:text-slate-100">{formatWorkedHours(r)}</td>
                      <td className="px-4 py-3 text-sm text-slate-700 dark:text-slate-200">{r.status}</td>
                      <td className="px-4 py-3">
                        {earlyOut ? <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-rose-100 text-rose-700">Early Departure</span> : <span className="text-xs text-slate-400">—</span>}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Attendance Records */}
      {!monthlyReport && (
      <div className="bg-white dark:bg-slate-800 rounded-xl border border-slate-100 dark:border-slate-700 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-slate-50 dark:bg-slate-900 border-b border-slate-200 dark:border-slate-700">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-semibold text-slate-700 dark:text-slate-300 uppercase">Date</th>
                <th className="px-6 py-3 text-left text-xs font-semibold text-slate-700 dark:text-slate-300 uppercase">Employee</th>
                <th className="px-6 py-3 text-left text-xs font-semibold text-slate-700 dark:text-slate-300 uppercase">Clock In</th>
                <th className="px-6 py-3 text-left text-xs font-semibold text-slate-700 dark:text-slate-300 uppercase">Clock Out</th>
                <th className="px-6 py-3 text-left text-xs font-semibold text-slate-700 dark:text-slate-300 uppercase">Working Hours</th>
                <th className="px-6 py-3 text-left text-xs font-semibold text-slate-700 dark:text-slate-300 uppercase">Status</th>
                <th className="px-6 py-3 text-left text-xs font-semibold text-slate-700 dark:text-slate-300 uppercase">Notes</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100 dark:divide-slate-700">
              {(isManager ? dateFilteredAttendance : attendance).map((record) => {
                // Iter84 — Manager view's `employees` list only contains
                // direct reports, NOT the manager themself. Fall back to
                // the logged-in user's profile when the row is the
                // manager's own clock-in (reported by Ankur 03-Jun-2026:
                // "Lee Wei Ming displayed as Unknown").
                let employee = employees.find(e => e.employee_id === record.employee_id);
                if (!employee && record.employee_id === user.employee_id) {
                  employee = { full_name: user.full_name, employee_id: user.employee_id };
                }
                const workingHours = formatWorkedHours(record);
                const earlyOut = isEarlyDeparture(record);
                const dateKey = (record.date || '').slice(0, 10);
                const onLeaveEmp = isOnLeaveOnDate(record.employee_id, dateKey, approvedLeaves);
                const holidayEmp = findPublicHoliday(dateKey, publicHolidays);
                const manualOverrideEmp = hasManualStatusOverride(record);
                // Iter88m — if the row was auto-resolved (employee was on
                // approved leave but physically attended), suppress the
                // "On Leave" / "Public Holiday" badges and show an info pill.
                const autoResolvedLeaveEmp = record.auto_resolved_leave;
                const showHolidayEmp = holidayEmp && !manualOverrideEmp && !autoResolvedLeaveEmp;
                const showOnLeaveEmp = onLeaveEmp && !manualOverrideEmp && !autoResolvedLeaveEmp;
                // Resolve the displayed status label (honours persisted manual edits).
                const empStatus = getAttendanceStatus(record);
                const empStatusStyle = getStatusBadge(empStatus);
                return (
                <tr 
                  key={record.attendance_id} 
                  data-testid={`attendance-${record.attendance_id}`} 
                  className="hover:bg-slate-50 dark:hover:bg-slate-700 cursor-pointer transition-colors"
                  onClick={() => {
                    const clockIn = formatAttendanceTime(record.clock_in, { withMeridiem: true });
                    const clockOut = formatAttendanceTime(record.clock_out, { withMeridiem: true });
                    // Iter81 — Display the raw `record.date` (YYYY-MM-DD)
                    // reformatted to DD/MM/YYYY without any new Date()
                    // conversion. Was previously `new Date(record.date)
                    // .toLocaleDateString('en-MY')` which silently shifted
                    // the date by one day for timezones west of UTC.
                    const parts = (record.date || '').slice(0, 10).split('-');
                    const dateLbl = parts.length === 3 ? `${parts[2]}/${parts[1]}/${parts[0]}` : (record.date || '');
                    toast.info(`Attendance: ${employee?.full_name || record.employee_id}\nDate: ${dateLbl}\nClock In: ${clockIn}\nClock Out: ${clockOut}\nWorked: ${workingHours}\nStatus: ${record.status}${earlyOut ? '\n⚠ Early departure' : ''}`);
                  }}
                  title="Click to view details"
                >
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-900 dark:text-slate-100">
                    {(() => {
                      // Iter81 — same wall-clock-safe date rendering for
                      // the row cell.
                      const parts = (record.date || '').slice(0, 10).split('-');
                      return parts.length === 3 ? `${parts[2]}/${parts[1]}/${parts[0]}` : (record.date || '-');
                    })()}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-900 dark:text-slate-100">
                    <div>
                      <div className="font-medium">{employee?.full_name || 'Unknown'}</div>
                      <div className="text-xs text-slate-500 dark:text-slate-400 font-mono">{record.employee_id}</div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-900 dark:text-slate-100">
                    {formatAttendanceTime(record.clock_in, { withMeridiem: true })}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-900 dark:text-slate-100">
                    {formatAttendanceTime(record.clock_out, { withMeridiem: true })}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-slate-700 dark:text-slate-200" data-testid={`worked-${record.attendance_id}`}>
                    {workingHours}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex flex-col gap-1">
                      {showHolidayEmp ? (
                        <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-indigo-100 text-indigo-700 dark:bg-indigo-900/40 dark:text-indigo-300 w-fit" title={holidayEmp.name}>
                          Public Holiday
                        </span>
                      ) : showOnLeaveEmp ? (
                        <span data-testid={`on-leave-emp-${record.attendance_id}`} className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-sky-100 text-sky-700 dark:bg-sky-900/40 dark:text-sky-300 w-fit">
                          On Leave
                        </span>
                      ) : (
                        <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${empStatusStyle.bg} ${empStatusStyle.text} w-fit`}>
                          {empStatusStyle.label}
                        </span>
                      )}
                      <HrMarkedPill record={record} />
                      {earlyOut && !showOnLeaveEmp && !showHolidayEmp && (
                        <span data-testid={`early-departure-emp-${record.attendance_id}`} className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-rose-100 text-rose-700 dark:bg-rose-900/40 dark:text-rose-300 w-fit">
                          Early Departure
                        </span>
                      )}
                      {/* Iter88m — Auto-resolved leave pill.
                          Iter53 (Bug 4) — Hide when HR manually overrode the status. */}
                      {autoResolvedLeaveEmp && !manualOverrideEmp && (
                        <span
                          data-testid={`auto-resolved-leave-emp-${record.attendance_id}`}
                          className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300 w-fit"
                          title={`Approved leave for this day was auto-cancelled because you were physically present. ${(autoResolvedLeaveEmp.days_refunded || 0)} day(s) refunded.`}
                        >
                          Was on leave · auto-cancelled
                        </span>
                      )}
                    </div>
                  </td>
                  <td className="px-6 py-4 text-sm text-slate-600 dark:text-slate-400">
                    {record.notes || '-'}
                  </td>
                </tr>
              );})}
            </tbody>
          </table>
        </div>
      </div>
      )}

      {!monthlyReport && (isManager ? dateFilteredAttendance : attendance).length === 0 && (
        <div className="text-center py-12 bg-white dark:bg-slate-800 rounded-xl border border-slate-100 dark:border-slate-700">
          <Calendar className="h-12 w-12 text-slate-300 dark:text-slate-600 mx-auto mb-3" />
          <p className="text-slate-500 dark:text-slate-400">
            {isManager ? `No attendance records found for ${new Date(dateFilter).toLocaleDateString('en-MY')}` : 'No attendance records found'}
          </p>
        </div>
      )}
    </div>
  );
}
